import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Grid,
  Link,
  Pagination,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import { toast } from 'react-toastify';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllProducts,
  createProduct,
  deleteProduct,
  reorderProducts,
  fetchAllProductOrders,
  fetchProductOrderById,
  cancelProductOrder,
  fetchProductLogoDefaults,
} from '../../controllers/paymentsController';
import { resolveProductImageUrl } from '../../api/httpClientPayments';
import {
  ProductOrderStatus,
  ProductOrderStatusNames,
  ProductPaymentMethodNames,
  LOW_STOCK_THRESHOLD,
  getProductOrderStatusChipSx,
} from './productOrderConstants';
import ProductOrderLogoInfoSection from './components/ProductOrderLogoInfoSection';
import CreateProductOrderDialog from './components/CreateProductOrderDialog';
import {
  FALLBACK_PRODUCT_LOGO_INCOME_ARP_CODE,
  normalizeLogoIncomeArpCode,
} from './productFormUtils';
import { formatMoneyWithCurrency, getCurrencyDisplayLabel, normalizeCurrencyInput } from '../../utils/currencyDisplay';

const cardSx = {
  border: '1px solid #e7e7e7',
  boxShadow: 'none',
  borderRadius: '8px',
  overflow: 'hidden',
};

const tableHeadCellSx = {
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '1px dashed #e8e8e8',
  px: 1.5,
  py: 1.5,
  color: '#a3a3a3',
};

const tableContainerPaperSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
  border: '1px solid #f5f5f5',
  borderRadius: '8px',
  overflow: 'hidden',
  py: 1,
  px: 2.5,
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '5px',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
  },
  '& .MuiInputBase-input': { py: 1, px: 1.5, fontSize: '14px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('tr-TR');
};

const formatMoney = formatMoneyWithCurrency;

const SortableRow = ({ product, onRowClick, onEdit, onDelete, isLast }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: product.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      onClick={onRowClick}
      hover
      sx={{
        cursor: 'pointer',
        '& .MuiTableCell-root': {
          borderBottom: isLast ? 'none' : '1px dashed #e8e8e8',
          px: 1.5,
          py: 1.5,
          color: '#444444',
          fontSize: '14px',
        },
        '&:hover': {
          backgroundColor: '#fafafa',
        },
      }}
    >
      <TableCell sx={{ width: 40, p: 1 }}>
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#a3a3a3',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DragIndicatorIcon sx={{ fontSize: 20 }} />
        </Box>
      </TableCell>
      <TableCell>
        <Typography sx={{ fontWeight: 500, color: '#444444', fontSize: 14 }}>{product.name}</Typography>
        {product.sku && (
          <Typography sx={{ fontSize: 11, color: '#a3a3a3' }}>SKU: {product.sku}</Typography>
        )}
      </TableCell>
      <TableCell>{product.category || '-'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>
        {product.price.toLocaleString('tr-TR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}{' '}
        {product.currency && getCurrencyDisplayLabel(product.currency)}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{product.stockQuantity != null ? product.stockQuantity : 'Sınırsız'}</span>
          {product.stockQuantity != null && product.stockQuantity < LOW_STOCK_THRESHOLD && (
            <Chip
              size="small"
              label="Düşük stok"
              sx={{
                height: 20,
                fontSize: '10px',
                fontWeight: 600,
                backgroundColor: '#FFF8DD',
                color: '#DFA000',
              }}
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={product.isActive ? 'Aktif' : 'Pasif'}
          sx={{
            height: 24,
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '12px',
            border: 'none',
            backgroundColor: product.isActive ? '#DFFFEA' : '#FFEEF3',
            color: product.isActive ? '#17C653' : '#F8285A',
          }}
        />
      </TableCell>
      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(product);
            }}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              backgroundColor: '#E7EDF4',
              color: '#0E4D94',
              '&:hover': { backgroundColor: '#CFDBEA' },
              '& svg': { fontSize: '16px' },
            }}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(product);
            }}
            sx={{
              width: 32,
              height: 32,
              borderRadius: '6px',
              backgroundColor: '#FEF0F2',
              color: '#F8285A',
              '&:hover': { backgroundColor: '#FDD9E1' },
              '& svg': { fontSize: '16px' },
            }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

const MiscPayments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [isActive, setIsActive] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: '',
    price: '',
    currency: 'TRY',
    stockQuantity: '',
    maxQuantityPerOrder: '',
    imageFile: null,
    isActive: true,
    logoIncomeArpCode: '',
  });
  const [saving, setSaving] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

  // Ürün Satışları tab
  const [tabValue, setTabValue] = useState(0);
  const [productSales, setProductSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesPage, setSalesPage] = useState(1);
  const [salesPageSize] = useState(10);
  const [salesTotalCount, setSalesTotalCount] = useState(0);
  const [salesTotalPages, setSalesTotalPages] = useState(1);
  const [pendingOrderCount, setPendingOrderCount] = useState(0);
  const [salesProductId, setSalesProductId] = useState('');
  const [salesStartDate, setSalesStartDate] = useState('');
  const [salesEndDate, setSalesEndDate] = useState('');
  const [salesStatus, setSalesStatus] = useState('');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [createOrderOpen, setCreateOrderOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await fetchAllProducts({
        search: search || null,
        category: category || null,
        isActive: isActive === '' ? null : isActive === 'true',
      });
      setProducts(Array.isArray(data) ? data : []);
    } catch (e) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, isActive]);

  const loadPendingCount = useCallback(async () => {
    try {
      const { meta } = await fetchAllProductOrders({
        pageNumber: 1,
        pageSize: 1,
        status: ProductOrderStatus.Pending,
      });
      setPendingOrderCount(meta?.totalCount ?? 0);
    } catch {
      setPendingOrderCount(0);
    }
  }, []);

  const loadProductSales = useCallback(async () => {
    setSalesLoading(true);
    try {
      const result = await fetchAllProductOrders({
        pageNumber: salesPage,
        pageSize: salesPageSize,
        status: salesStatus || null,
        productId: salesProductId || null,
        startDate: salesStartDate || null,
        endDate: salesEndDate || null,
      });
      const list = Array.isArray(result?.data) ? result.data : [];
      setProductSales(list);
      const total = result?.meta?.totalCount ?? list.length;
      setSalesTotalCount(total);
      setSalesTotalPages(result?.meta?.totalPages ?? Math.max(1, Math.ceil(total / salesPageSize)));
    } catch (e) {
      setProductSales([]);
      setSalesTotalCount(0);
      setSalesTotalPages(1);
    } finally {
      setSalesLoading(false);
    }
  }, [salesPage, salesPageSize, salesStatus, salesProductId, salesStartDate, salesEndDate]);

  const openDetail = useCallback(async (row) => {
    if (!row?.id) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await fetchProductOrderById(row.id);
      setDetail(data);
    } catch (e) {
      toast.error(e?.message || 'Sipariş detayı alınamadı');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleConfirmCancel = async () => {
    if (!cancelTarget?.id) return;
    setCancelProcessing(true);
    try {
      await cancelProductOrder({
        orderId: cancelTarget.id,
        cancellationReason: cancelReason || null,
      });
      toast.success('Sipariş iptal edildi');
      setCancelOpen(false);
      setCancelTarget(null);
      setCancelReason('');
      if (detailOpen && detail?.id === cancelTarget.id) {
        setDetailOpen(false);
        setDetail(null);
      }
      loadProductSales();
      loadPendingCount();
      loadProducts();
    } catch (e) {
      toast.error(e?.message || 'Sipariş iptal edilemedi');
    } finally {
      setCancelProcessing(false);
    }
  };

  useEffect(() => {
    if (tabValue === 1) {
      loadProductSales();
      loadPendingCount();
    }
  }, [tabValue, loadProductSales, loadPendingCount]);

  useEffect(() => {
    const orderId = searchParams.get('orderId');
    if (!orderId) return;
    setTabValue(1);
    openDetail({ id: orderId });
    const next = new URLSearchParams(searchParams);
    next.delete('orderId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, openDetail]);

  const uniqueCategories = Array.from(
    new Set(products.map((p) => p.category).filter((c) => c && c.trim() !== ''))
  );

  const handleOpenCreate = async () => {
    setImagePreviewUrl(null);
    let defaultLogoIncomeArpCode = FALLBACK_PRODUCT_LOGO_INCOME_ARP_CODE;
    try {
      const defaults = await fetchProductLogoDefaults();
      defaultLogoIncomeArpCode = defaults?.defaultLogoIncomeArpCode || defaultLogoIncomeArpCode;
    } catch {
      // API yoksa fallback kullan
    }
    setForm({
      name: '',
      description: '',
      category: '',
      price: '',
      currency: 'TRY',
      stockQuantity: '',
      maxQuantityPerOrder: '',
      imageFile: null,
      isActive: true,
      logoIncomeArpCode: defaultLogoIncomeArpCode,
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (p) => {
    navigate(`/payments/misc/${p.id}`);
  };

  const handleChange = (key) => (e) => {
    const value = key === 'isActive' ? e.target.value === 'true' : e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    if (file) {
      setImagePreviewUrl(URL.createObjectURL(file));
      setForm((prev) => ({ ...prev, imageFile: file }));
    } else {
      setForm((prev) => ({ ...prev, imageFile: null }));
    }
  };

  const handleClearImage = () => {
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setForm((prev) => ({ ...prev, imageFile: null }));
  };

  const handleSave = async () => {
    if (!form.name || !form.price) {
      toast.error('İsim ve fiyat zorunludur.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description?.trim() || null,
        category: form.category?.trim() || null,
        price: Number(form.price),
        currency: form.currency || 'TRY',
        stockQuantity: form.stockQuantity !== '' ? Number(form.stockQuantity) : null,
        maxQuantityPerOrder:
          form.maxQuantityPerOrder !== '' ? Number(form.maxQuantityPerOrder) : null,
        isActive: form.isActive,
        logoIncomeArpCode: normalizeLogoIncomeArpCode(form.logoIncomeArpCode),
      };
      await createProduct({
        ...payload,
        order: 1,
        imageFile: form.imageFile,
      });
      toast.success('Ürün oluşturuldu');
      setDialogOpen(false);
      await loadProducts();
    } catch (e) {
      toast.error(e?.message || 'Kayıt işlemi başarısız oldu');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`${p.name} ürününü silmek istediğinize emin misiniz?`)) return;
    try {
      await deleteProduct(p.id);
      toast.success('Ürün silindi');
      await loadProducts();
    } catch (e) {
      toast.error(e?.message || 'Ürün silinemedi');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = products.findIndex((p) => p.id === active.id);
    const newIndex = products.findIndex((p) => p.id === over.id);
    const newOrder = arrayMove(products, oldIndex, newIndex);
    
    // Store original order for revert
    const originalOrder = [...products];
    
    // Optimistically update UI
    setProducts(newOrder);

    try {
      // Send reorder request to backend
      const reorderItems = newOrder.map((p, index) => ({
        ProductId: p.id,
        Order: index + 1,
      }));
      await reorderProducts(reorderItems);
      toast.success('Ürün sıralaması güncellendi');
    } catch (e) {
      // Revert on error
      setProducts(originalOrder);
      toast.error(e?.message || 'Ürün sıralaması güncellenemedi');
    }
  };

  const handleCloseDialog = () => {
    if (saving) return;
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    setDialogOpen(false);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500, color: '#444444' }}>
          Çeşitli Ödemeler
        </Typography>
      </Stack>

      <Tabs
        value={tabValue}
        onChange={(_, v) => setTabValue(v)}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#a3a3a3',
            minHeight: 48,
            '&.Mui-selected': { color: '#0E4D94' },
          },
          '& .MuiTabs-indicator': { backgroundColor: '#0E4D94' },
        }}
      >
        <Tab label="Ürünler" />
        <Tab label="Ürün Satışları" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#444444' }}>
              Ürün listesi
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ width: 16 }} />}
              sx={{
                textTransform: 'none',
                backgroundColor: '#E7EDF4',
                boxShadow: 'none',
                fontSize: 13,
                borderRadius: '5px',
                color: '#0E4D94',
                '&:hover': {
                  backgroundColor: '#CFDBEA',
                  boxShadow: 'none',
                },
              }}
              onClick={handleOpenCreate}
            >
              Yeni Ürün
            </Button>
          </Stack>

          <Card sx={{ mb: 2, ...cardSx }}>
            <CardContent sx={{ py: 2, px: 3 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', maxWidth: 720 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                      Arama (isim)
                    </Typography>
                    <TextField
                      size="small"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      fullWidth
                      placeholder=""
                      sx={textFieldSx}
                    />
                  </Box>
                  <Box sx={{ minWidth: { sm: 160 } }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                      Kategori
                    </Typography>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      sx={textFieldSx}
                    >
                      <MenuItem value="">Tümü</MenuItem>
                      {uniqueCategories.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box sx={{ minWidth: { sm: 140 } }}>
                    <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>
                      Durum
                    </Typography>
                    <TextField
                      size="small"
                      select
                      fullWidth
                      value={isActive}
                      onChange={(e) => setIsActive(e.target.value)}
                      sx={textFieldSx}
                    >
                      <MenuItem value="">Tümü</MenuItem>
                      <MenuItem value="true">Aktif</MenuItem>
                      <MenuItem value="false">Pasif</MenuItem>
                    </TextField>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {loading ? (
            <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadCellSx, width: 40 }} />
                    <TableCell sx={tableHeadCellSx}>Ürün</TableCell>
                    <TableCell sx={tableHeadCellSx}>Kategori</TableCell>
                    <TableCell sx={tableHeadCellSx}>Fiyat</TableCell>
                    <TableCell sx={tableHeadCellSx}>Stok</TableCell>
                    <TableCell sx={tableHeadCellSx}>Durum</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>
                      İşlemler
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow
                      key={`sk-${index}`}
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: index === 4 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell><Skeleton width={24} height={24} /></TableCell>
                      <TableCell><Skeleton width={140} height={20} /></TableCell>
                      <TableCell><Skeleton width={72} height={20} /></TableCell>
                      <TableCell><Skeleton width={88} height={20} /></TableCell>
                      <TableCell><Skeleton width={36} height={20} /></TableCell>
                      <TableCell><Skeleton width={56} height={24} borderRadius={12} /></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Skeleton width={32} height={32} borderRadius={6} />
                          <Skeleton width={32} height={32} borderRadius={6} />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={{ ...tableHeadCellSx, width: 40 }} />
                    <TableCell sx={tableHeadCellSx}>Ürün</TableCell>
                    <TableCell sx={tableHeadCellSx}>Kategori</TableCell>
                    <TableCell sx={tableHeadCellSx}>Fiyat</TableCell>
                    <TableCell sx={tableHeadCellSx}>Stok</TableCell>
                    <TableCell sx={tableHeadCellSx}>Durum</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>
                      İşlemler
                    </TableCell>
                  </TableRow>
                </TableHead>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <TableBody>
                    {products.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                            Kayıtlı ürün bulunmuyor.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <SortableContext items={products.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                        {products.map((p, i) => (
                          <SortableRow
                            key={p.id}
                            product={p}
                            isLast={i === products.length - 1}
                            onRowClick={() => handleOpenEdit(p)}
                            onEdit={handleOpenEdit}
                            onDelete={handleDelete}
                          />
                        ))}
                      </SortableContext>
                    )}
                  </TableBody>
                </DndContext>
              </Table>
            </TableContainer>
          )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Yeni Ürün
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            disabled={saving}
            sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Ürün Adı</Typography>
              <TextField
                value={form.name}
                onChange={handleChange('name')}
                fullWidth
                required
                size="small"
                placeholder=""
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Açıklama</Typography>
              <TextField
                value={form.description}
                onChange={handleChange('description')}
                fullWidth
                multiline
                rows={3}
                size="small"
                placeholder=""
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Kategori</Typography>
              <TextField
                value={form.category}
                onChange={handleChange('category')}
                fullWidth
                size="small"
                placeholder=""
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Logo Gelir Hesabı</Typography>
              <TextField
                value={form.logoIncomeArpCode}
                onChange={handleChange('logoIncomeArpCode')}
                fullWidth
                size="small"
                placeholder="603.003.110"
                inputProps={{ maxLength: 50 }}
                helperText="Boş bırakılırsa sistem varsayılanı kullanılır."
                sx={textFieldSx}
              />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Fiyat</Typography>
                <TextField
                  type="number"
                  value={form.price}
                  onChange={handleChange('price')}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ min: 0, step: '0.01' }}
                  sx={textFieldSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Para Birimi</Typography>
                <TextField
                  value={getCurrencyDisplayLabel(form.currency || 'TRY')}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    currency: normalizeCurrencyInput(e.target.value) || 'TRY',
                  }))}
                  fullWidth
                  size="small"
                  sx={textFieldSx}
                />
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Stok Miktarı</Typography>
                <TextField
                  type="number"
                  value={form.stockQuantity}
                  onChange={handleChange('stockQuantity')}
                  fullWidth
                  size="small"
                  sx={textFieldSx}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Sipariş Başına Max Adet</Typography>
                <TextField
                  type="number"
                  value={form.maxQuantityPerOrder}
                  onChange={handleChange('maxQuantityPerOrder')}
                  fullWidth
                  size="small"
                  sx={textFieldSx}
                />
              </Box>
            </Stack>
            <Box sx={{ width: '100%' }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Ürün Görseli</Typography>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    borderRadius: '5px',
                    borderColor: '#e6e6e6',
                    color: '#444444',
                    '&:hover': { borderColor: '#0E4D94', backgroundColor: '#E7EDF4' },
                  }}
                >
                  Dosya Seç
                  <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                </Button>
                {form.imageFile && (
                  <Typography variant="body2" color="text.secondary">
                    {form.imageFile.name}
                  </Typography>
                )}
              </Stack>
              {imagePreviewUrl && (
                <Box sx={{ mt: 2 }}>
                  <Box
                    component="img"
                    src={imagePreviewUrl}
                    alt="Önizleme"
                    sx={{
                      display: 'block',
                      maxHeight: 180,
                      maxWidth: '100%',
                      borderRadius: 1,
                      border: '1px solid #e7e7e7',
                      objectFit: 'contain',
                    }}
                  />
                  <Button
                    size="small"
                    onClick={handleClearImage}
                    sx={{ mt: 1, textTransform: 'none', fontSize: '13px', color: '#666' }}
                  >
                    Görseli Kaldır
                  </Button>
                </Box>
              )}
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Durum</Typography>
              <TextField
                select
                value={String(form.isActive)}
                onChange={handleChange('isActive')}
                fullWidth
                size="small"
                sx={textFieldSx}
              >
                <MenuItem value="true">Aktif</MenuItem>
                <MenuItem value="false">Pasif</MenuItem>
              </TextField>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button
            onClick={() => !saving && setDialogOpen(false)}
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              backgroundColor: '#0E4D94',
              borderRadius: '5px',
              px: 2,
              '&:hover': { backgroundColor: '#0a3d7a' },
            }}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}

      {tabValue === 1 && (
        <>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#444444' }}>
                Ürün satışları
              </Typography>
              {pendingOrderCount > 0 && (
                <Chip
                  size="small"
                  label={`${pendingOrderCount} beklemede`}
                  onClick={() => { setSalesStatus(String(ProductOrderStatus.Pending)); setSalesPage(1); }}
                  sx={{
                    height: 24,
                    fontSize: '12px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: '#E7EDF4',
                    color: '#0E4D94',
                  }}
                />
              )}
            </Stack>
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ width: 16 }} />}
              onClick={() => setCreateOrderOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: '#E7EDF4',
                boxShadow: 'none',
                fontSize: 13,
                borderRadius: '5px',
                color: '#0E4D94',
                '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
              }}
            >
              Manuel Satış
            </Button>
          </Stack>

          <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
            Online PayTR (kart) ödemeleri callback ile Logo&apos;ya otomatik aktarılır (borç + tahsilat fişi).
            Admin manuel satışlar Logo&apos;ya gitmez.
          </Alert>

          <Card sx={{ mb: 2, ...cardSx }}>
            <CardContent sx={{ py: 2, px: 3 }}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', lg: 'flex-end' }}
                sx={{ flexWrap: 'wrap' }}
              >
                <Box sx={{ minWidth: { lg: 200 } }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Ürün</Typography>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={salesProductId}
                    onChange={(e) => { setSalesProductId(e.target.value); setSalesPage(1); }}
                    sx={textFieldSx}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    {products.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ minWidth: { lg: 140 } }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Durum</Typography>
                  <TextField
                    size="small"
                    select
                    fullWidth
                    value={salesStatus}
                    onChange={(e) => { setSalesStatus(e.target.value); setSalesPage(1); }}
                    sx={textFieldSx}
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    <MenuItem value="0">Beklemede</MenuItem>
                    <MenuItem value="1">Ödendi</MenuItem>
                    <MenuItem value="2">İptal</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ minWidth: { lg: 160 } }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Başlangıç Tarihi</Typography>
                  <TextField
                    size="small"
                    type="date"
                    fullWidth
                    value={salesStartDate}
                    onChange={(e) => setSalesStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Box>
                <Box sx={{ minWidth: { lg: 160 } }}>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500 }}>Bitiş Tarihi</Typography>
                  <TextField
                    size="small"
                    type="date"
                    fullWidth
                    value={salesEndDate}
                    onChange={(e) => setSalesEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={loadProductSales}
                  sx={{
                    textTransform: 'none',
                    fontSize: '13px',
                    borderRadius: '5px',
                    borderColor: '#e6e6e6',
                    color: '#444444',
                    px: 2,
                    py: 1.1,
                    alignSelf: { xs: 'stretch', lg: 'center' },
                    '&:hover': { borderColor: '#0E4D94', backgroundColor: '#E7EDF4' },
                  }}
                >
                  Filtrele
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {salesLoading ? (
            <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Tarih</TableCell>
                    <TableCell sx={tableHeadCellSx}>Ürün</TableCell>
                    <TableCell sx={tableHeadCellSx}>Miktar</TableCell>
                    <TableCell sx={tableHeadCellSx}>Birim Fiyat</TableCell>
                    <TableCell sx={tableHeadCellSx}>Toplam</TableCell>
                    <TableCell sx={tableHeadCellSx}>Alıcı</TableCell>
                    <TableCell sx={tableHeadCellSx}>Durum</TableCell>
                    <TableCell sx={tableHeadCellSx}>Ödeme</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow
                      key={`ssk-${index}`}
                      sx={{
                        '& .MuiTableCell-root': {
                          borderBottom: index === 4 ? 'none' : '1px dashed #e8e8e8',
                          px: 1.5,
                          py: 1.5,
                        },
                      }}
                    >
                      <TableCell><Skeleton width={120} height={20} /></TableCell>
                      <TableCell><Skeleton width={100} height={20} /></TableCell>
                      <TableCell><Skeleton width={32} height={20} /></TableCell>
                      <TableCell><Skeleton width={72} height={20} /></TableCell>
                      <TableCell><Skeleton width={72} height={20} /></TableCell>
                      <TableCell><Skeleton width={100} height={20} /></TableCell>
                      <TableCell><Skeleton width={72} height={20} /></TableCell>
                      <TableCell><Skeleton width={80} height={20} /></TableCell>
                      <TableCell align="right"><Skeleton width={48} height={28} borderRadius={4} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={tableContainerPaperSx}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={tableHeadCellSx}>Tarih</TableCell>
                    <TableCell sx={tableHeadCellSx}>Ürün</TableCell>
                    <TableCell sx={tableHeadCellSx}>Miktar</TableCell>
                    <TableCell sx={tableHeadCellSx}>Birim Fiyat</TableCell>
                    <TableCell sx={tableHeadCellSx}>Toplam</TableCell>
                    <TableCell sx={tableHeadCellSx}>Alıcı</TableCell>
                    <TableCell sx={tableHeadCellSx}>Durum</TableCell>
                    <TableCell sx={tableHeadCellSx}>Ödeme</TableCell>
                    <TableCell align="right" sx={tableHeadCellSx}>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productSales.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>
                          Sipariş bulunmuyor.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    productSales.map((sale, index) => {
                      const chipSx = getProductOrderStatusChipSx(sale.status);
                      const canCancel = sale.status !== ProductOrderStatus.Cancelled;
                      return (
                        <TableRow
                          key={sale.id}
                          hover
                          onClick={() => openDetail(sale)}
                          sx={{
                            cursor: 'pointer',
                            '& .MuiTableCell-root': {
                              borderBottom: index === productSales.length - 1 ? 'none' : '1px dashed #e8e8e8',
                              px: 1.5,
                              py: 1.5,
                              color: '#444444',
                              fontSize: '14px',
                            },
                            '&:hover': { backgroundColor: '#fafafa' },
                          }}
                        >
                          <TableCell>{formatDateTime(sale.createdAt)}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{sale.productName ?? '-'}</TableCell>
                          <TableCell>{sale.quantity ?? '-'}</TableCell>
                          <TableCell>{formatMoney(sale.unitPrice, sale.currency)}</TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>{formatMoney(sale.totalAmount, sale.currency)}</TableCell>
                          <TableCell>{sale.buyerFullName ?? '-'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={sale.statusName || ProductOrderStatusNames[sale.status] || '-'}
                              sx={{ height: 24, fontSize: '12px', fontWeight: 600, borderRadius: '12px', ...chipSx }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '13px' }}>
                              {sale.paymentMethodName || ProductPaymentMethodNames[sale.paymentMethod] || '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#a3a3a3' }}>
                              {formatDateTime(sale.paymentDate)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Detay">
                                <IconButton size="small" onClick={() => openDetail(sale)}>
                                  <VisibilityIcon sx={{ fontSize: 18, color: '#0E4D94' }} />
                                </IconButton>
                              </Tooltip>
                              {canCancel && (
                                <Tooltip title="İptal">
                                  <IconButton
                                    size="small"
                                    onClick={() => { setCancelTarget(sale); setCancelOpen(true); }}
                                  >
                                    <CancelIcon sx={{ fontSize: 18, color: '#F8285A' }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {salesTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination
                count={salesTotalPages}
                page={salesPage}
                onChange={(_, v) => setSalesPage(v)}
                size="small"
              />
            </Box>
          )}

          <CreateProductOrderDialog
            open={createOrderOpen}
            onClose={() => setCreateOrderOpen(false)}
            onCreated={() => {
              loadProductSales();
              loadPendingCount();
              loadProducts();
            }}
            products={products}
          />

          <Dialog open={detailOpen} onClose={() => { setDetailOpen(false); setDetail(null); }} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', pr: 3 }}>
              Ürün Siparişi Detay
              <IconButton onClick={() => { setDetailOpen(false); setDetail(null); }} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 2 }}>
              {detailLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : detail ? (
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    {detail.productImageUrl && (
                      <Grid item xs={12} sm={4}>
                        <Box
                          component="img"
                          src={resolveProductImageUrl(detail.productImageUrl)}
                          alt={detail.productName}
                          sx={{
                            width: '100%',
                            maxHeight: 160,
                            objectFit: 'contain',
                            borderRadius: 1,
                            border: '1px solid #ececec',
                          }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} sm={detail.productImageUrl ? 8 : 12}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Ürün</Typography>
                          {detail.productId ? (
                            <Link
                              component={RouterLink}
                              to={`/payments/misc/${detail.productId}`}
                              sx={{ fontSize: '14px', fontWeight: 600 }}
                            >
                              {detail.productName || detail.productId}
                            </Link>
                          ) : (
                            <Typography sx={{ fontSize: '14px' }}>{detail.productName || '-'}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Alıcı</Typography>
                          {detail.buyerPersonId ? (
                            <Link
                              component={RouterLink}
                              to={`/members/${detail.buyerPersonId}`}
                              sx={{ fontSize: '14px', fontWeight: 500 }}
                            >
                              {detail.buyerFullName}
                            </Link>
                          ) : (
                            <Typography sx={{ fontSize: '14px' }}>{detail.buyerFullName || '-'}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Sipariş</Typography>
                          <Typography sx={{ fontSize: '14px' }}>
                            {detail.quantity} × {formatMoney(detail.unitPrice, detail.currency)}
                            {' = '}
                            <Box component="span" sx={{ fontWeight: 600, color: '#0E4D94' }}>
                              {formatMoney(detail.totalAmount, detail.currency)}
                            </Box>
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Durum</Typography>
                          <Chip
                            size="small"
                            label={detail.statusName || ProductOrderStatusNames[detail.status]}
                            sx={{ ...getProductOrderStatusChipSx(detail.status), fontWeight: 600 }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Ödeme yöntemi</Typography>
                          <Typography sx={{ fontSize: '14px' }}>
                            {detail.paymentMethodName || ProductPaymentMethodNames[detail.paymentMethod] || '-'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Ödeme tarihi</Typography>
                          <Typography sx={{ fontSize: '14px' }}>{formatDateTime(detail.paymentDate)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>Oluşturulma</Typography>
                          <Typography sx={{ fontSize: '14px' }}>{formatDateTime(detail.createdAt)}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  {(detail.payTrMerchantOid || detail.payTrPaymentType) && (
                    <Card variant="outlined" sx={{ boxShadow: 'none', borderColor: '#ececec' }}>
                      <CardContent>
                        <Typography sx={{ fontSize: '15px', fontWeight: 600, mb: 1 }}>PayTR</Typography>
                        <Stack spacing={0.5}>
                          {detail.payTrMerchantOid && (
                            <Typography sx={{ fontSize: '13px' }}>
                              Sipariş: <Box component="span" sx={{ fontFamily: 'monospace' }}>{detail.payTrMerchantOid}</Box>
                            </Typography>
                          )}
                          {detail.payTrPaymentType && (
                            <Typography sx={{ fontSize: '13px' }}>Tip: {detail.payTrPaymentType}</Typography>
                          )}
                          {detail.payTrCallbackReceivedAt && (
                            <Typography sx={{ fontSize: '13px' }}>
                              Callback: {formatDateTime(detail.payTrCallbackReceivedAt)}
                            </Typography>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  )}

                  <ProductOrderLogoInfoSection detail={detail} />
                </Stack>
              ) : null}
            </DialogContent>
            {detail && detail.status !== ProductOrderStatus.Cancelled && (
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => { setCancelTarget(detail); setCancelOpen(true); }}
                  sx={{ textTransform: 'none', color: '#F8285A' }}
                >
                  Siparişi İptal Et
                </Button>
              </DialogActions>
            )}
          </Dialog>

          <Dialog open={cancelOpen} onClose={() => !cancelProcessing && setCancelOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Siparişi İptal Et</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {cancelTarget?.status === ProductOrderStatus.Paid && (
                  <Alert severity="warning" sx={{ fontSize: '13px' }}>
                    Bu sipariş ödenmiş durumda. İptal edilirse stok iade edilecektir. Logo ters kayıt oluşturulmaz.
                  </Alert>
                )}
                <TextField
                  label="İptal nedeni (opsiyonel)"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  sx={textFieldSx}
                />
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setCancelOpen(false)} disabled={cancelProcessing} sx={{ textTransform: 'none' }}>
                Vazgeç
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmCancel}
                disabled={cancelProcessing}
                sx={{ textTransform: 'none', backgroundColor: '#F8285A' }}
              >
                {cancelProcessing ? <CircularProgress size={18} color="inherit" /> : 'İptal Et'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default MiscPayments;

